import React, { useState, useEffect, useMemo } from "react";
import { DashboardStatsSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { TransactionApi } from "@/api/transaction.api";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { MetricsCard } from "@/components/ui/metrics-card";
import { Handshake, CreditCard, BarChart2, Star } from "lucide-react";
import { endOfDay, startOfDay, subMonths } from "date-fns";
import { DateRange } from "react-day-picker";
import { DashboardDateRangePicker } from "@/components/ui/dashboard-date-range-picker";
import { toast } from "sonner";

const getDefaultDateRange = (): DateRange => ({
  from: startOfDay(subMonths(new Date(), 3)),
  to: endOfDay(new Date()),
});

function formatYAxisTick(value: number) {
  if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(0) + "k";
  }
  return value.toString();
}

const XpandPointDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [currency, setCurrency] = useState<any>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    getDefaultDateRange,
  );

  useEffect(() => {
    if (!dateRange?.from || !dateRange?.to) {
      return;
    }

    const startDateInMillis = startOfDay(dateRange.from).getTime();
    const endDateInMillis = endOfDay(dateRange.to).getTime();

    async function fetchStats() {
      setLoading(true);
      let clientId: number | null = null;
      try {
        const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
        if (userInfo?.roleCode === "PROGRAM_ADMIN" && userInfo?.clientId) {
          clientId = userInfo.clientId;
        }
        const res = await TransactionApi.getGeneralStats({
          clientId,
          programType: "TRANSFER",
          startDateInMillis,
          endDateInMillis,
        });
        setStats(res?.data?.generalStatsData || null);
        setCurrency(res?.data?.currencyVO || null);
      } catch {
        setStats(null);
        setCurrency(null);
        toast.error("Failed to fetch dashboard data.");
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, [dateRange]);

  const currencyCode = currency?.iso3CurrencyCode || "";

  const pointsAndRevenueData = useMemo(
    () => [
      {
        name: "Points Exchanged",
        value:
          Number(
            stats?.totalPointsRedeemed ??
              stats?.totalPointsAccruedBreakdown ??
              0,
          ) || 0,
      },
      {
        name: "Equivalent Amount",
        value:
          Number(
            stats?.totalPointsRedeemedEquivalentAmount ??
              stats?.cashbackAmountBreakdown ??
              0,
          ) || 0,
      },
    ],
    [stats],
  );

  const newVsRepeatedData = useMemo(
    () => [
      {
        name: "New",
        value: Number(stats?.newTransactions) || 0,
      },
      {
        name: "Repeated",
        value: Number(stats?.repeatedTransactions) || 0,
      },
    ],
    [stats],
  );

  return (
    <div className="space-y-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <div className="w-full sm:w-auto sm:min-w-[360px]">
          <DashboardDateRangePicker
            date={dateRange}
            setDate={setDateRange}
            placeholder="Select date"
          />
        </div>
      </div>
      {loading ? (
        <DashboardStatsSkeleton />
      ) : stats ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricsCard
              title="Transfer Partners"
              value={
                (
                  stats.totalTransferPartners ??
                  stats.totalTransferPrograms ??
                  0
                ).toLocaleString()
              }
              icon={<Handshake className="h-5 w-5" />}
            />
            <MetricsCard
              title="Exchange Transactions"
              value={stats.totalTransactions?.toLocaleString() ?? 0}
              icon={<CreditCard className="h-5 w-5" />}
            />
            <MetricsCard
              title="Points Exchanged"
              value={stats.totalPointsRedeemed?.toLocaleString() ?? 0}
              icon={<Star className="h-5 w-5" />}
            />
            <MetricsCard
              title="Revenue"
              value={
                <>
                  {(stats.revenue ?? 0).toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                  })}{" "}
                  <span className="text-sm font-normal">{currencyCode}</span>
                </>
              }
              icon={<BarChart2 className="h-5 w-5" />}
            />
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="p-4 sm:p-6 bg-white rounded-lg shadow border">
              <h2 className="text-xl font-semibold mb-4">
                Points Exchanged & Equivalent Amount
              </h2>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pointsAndRevenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={formatYAxisTick} fontSize={12} />
                    <Tooltip
                      formatter={(val: any, _name: any, props: any) => {
                        const label = props?.payload?.name;
                        if (label === "Equivalent Amount") {
                          return [
                            `${Number(val).toLocaleString("en-US", {
                              maximumFractionDigits: 2,
                            })} ${currencyCode}`.trim(),
                            label,
                          ];
                        }
                        return [Number(val).toLocaleString(), label];
                      }}
                    />
                    <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="p-4 sm:p-6 bg-white rounded-lg shadow border">
              <h2 className="text-xl font-semibold mb-4">
                New vs Repeated Transactions
              </h2>
              <div className="h-[300px] w-full">
                {newVsRepeatedData.some((item) => item.value > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={newVsRepeatedData}
                        cx="50%"
                        cy="55%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                        paddingAngle={2}
                      >
                        <Cell fill="#3b82f6" stroke="#fff" strokeWidth={2} />
                        <Cell fill="#22c55e" stroke="#fff" strokeWidth={2} />
                      </Pie>
                      <Tooltip
                        formatter={(val: any) => [
                          Number(val).toLocaleString(),
                          "Transactions",
                        ]}
                      />
                      <Legend verticalAlign="top" align="center" />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center text-gray-500">
                    No transaction mix data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center text-gray-500">No data available</div>
      )}
    </div>
  );
};

export default XpandPointDashboard;
